import { Lock, ShieldCheck } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { LanguageSwitcher } from "@/components/portal/LanguageSwitcher";

// Premium dark auth shell — shared by the KEWAGPortal Control OS entry (`/`) and the
// per-brand auth screens (`/p/:portalKey/auth`). Accent, heading font and hero image are
// theme-/config-driven (multi-tenant); the chrome stays a strong dark SaaS aesthetic.
export const AUTH = {
  bg: "#0B0D10",
  card: "#111418",
  cardBorder: "rgba(255,255,255,0.08)",
  text: "#F5F7FA",
  muted: "#93A0B4",
  inputBg: "#0E1116",
  inputBorder: "rgba(255,255,255,0.10)",
};

export function AuthLabel({ children, htmlFor }) {
  return (
    <label htmlFor={htmlFor} className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-1.5" style={{ color: AUTH.muted }}>
      {children}
    </label>
  );
}

export function AuthInput({ className = "", style, ...props }) {
  return (
    <input
      {...props}
      className={"kw-auth-input w-full h-12 px-4 text-sm rounded-xl outline-none transition-all " + className}
      style={{ background: AUTH.inputBg, border: `1px solid ${AUTH.inputBorder}`, color: AUTH.text, ...style }}
    />
  );
}

export function AuthPrimaryButton({ className = "", children, ...props }) {
  return (
    <button
      {...props}
      className={"w-full h-12 rounded-xl font-semibold text-sm transition-transform duration-150 active:scale-[.99] disabled:opacity-60 disabled:pointer-events-none hover:brightness-[1.05] " + className}
      style={{ background: "var(--accent)", color: "var(--accent-fg)" }}
    >
      {children}
    </button>
  );
}

export function AuthOutlineButton({ className = "", children, ...props }) {
  return (
    <button
      {...props}
      className={"w-full h-12 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-colors hover:bg-white/[.04] " + className}
      style={{ background: AUTH.inputBg, border: `1px solid ${AUTH.inputBorder}`, color: AUTH.text }}
    >
      {children}
    </button>
  );
}

export function AuthTabs({ mode, onChange, testidPrefix = "entry" }) {
  const { t } = useI18n();
  return (
    <div className="flex p-1 gap-1 rounded-xl" style={{ background: "rgba(255,255,255,.05)" }}>
      {["login", "register"].map((m) => (
        <button
          key={m}
          type="button"
          data-testid={`${testidPrefix}-tab-${m}`}
          onClick={() => onChange(m)}
          className="flex-1 py-2 text-sm font-semibold rounded-lg transition-colors"
          style={mode === m ? { background: "var(--accent)", color: "var(--accent-fg)" } : { color: AUTH.muted }}
        >
          {m === "login" ? t("auth.signIn") : t("auth.signUp")}
        </button>
      ))}
    </div>
  );
}

export function AuthShell({ brandName, brandTag, heroKicker, heroTitle, heroSubline, chips = [], heroImage, children }) {
  const { t } = useI18n();
  return (
    <div className="min-h-screen w-full lg:grid lg:grid-cols-[1.08fr_1fr]" style={{ background: AUTH.bg, color: AUTH.text }} data-testid="auth-shell">
      <style>{`.kw-auth-input:focus{border-color:var(--accent)!important;box-shadow:0 0 0 3px color-mix(in srgb,var(--accent) 22%,transparent)}.kw-auth-input::placeholder{color:${AUTH.muted};opacity:.7}`}</style>

      {/* HERO (desktop) */}
      <div className="relative hidden lg:block overflow-hidden">
        {heroImage && <img src={heroImage} alt="" className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(11,13,16,.62) 0%, rgba(11,13,16,.34) 42%, rgba(11,13,16,.94) 100%)" }} />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 xl:p-16">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
              <span className="font-heading text-lg font-bold">K</span>
            </span>
            <span className="font-heading text-xl font-bold tracking-tight">{brandName}</span>
            {brandTag && <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full" style={{ background: "rgba(255,255,255,.08)", color: "rgba(245,247,250,.75)" }}>{brandTag}</span>}
          </div>

          <div className="max-w-xl">
            {heroKicker && <div className="text-xs font-semibold uppercase tracking-[0.3em] mb-5" style={{ color: "var(--accent)" }}>{heroKicker}</div>}
            <h1 className="font-heading text-4xl xl:text-[3.4rem] font-bold leading-[1.06]">{heroTitle}</h1>
            {heroSubline && <p className="mt-6 text-base xl:text-lg max-w-lg leading-relaxed" style={{ color: "rgba(245,247,250,.72)" }}>{heroSubline}</p>}
            {chips.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2">
                {chips.map((c) => (
                  <span key={c} className="text-[11px] font-semibold tracking-[0.12em] px-3 py-1.5 rounded-full" style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.13)", color: "rgba(245,247,250,.88)" }}>
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs" style={{ color: AUTH.muted }}>
            <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> {t("authx.encrypted")}</span>
            <span className="opacity-40">·</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> {t("authx.securedArea")}</span>
          </div>
        </div>
      </div>

      {/* FORM SIDE */}
      <div className="relative flex flex-col min-h-screen">
        <div className="flex items-center gap-3 p-5 sm:p-6">
          <div className="lg:hidden flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
              <span className="font-heading text-base font-bold">K</span>
            </span>
            <span className="font-heading text-lg font-bold">{brandName}</span>
          </div>
          <div className="ml-auto"><LanguageSwitcher variant="dark" /></div>
        </div>

        <div className="flex-1 flex items-center justify-center px-5 sm:px-8 pb-12">
          <div className="w-full max-w-[440px] kw-fade-up">
            <div className="rounded-2xl p-7 sm:p-9" style={{ background: AUTH.card, border: `1px solid ${AUTH.cardBorder}`, boxShadow: "0 24px 70px -24px rgba(0,0,0,.7)" }}>
              {children}
            </div>
            <div className="mt-5 flex lg:hidden items-center justify-center gap-3 text-xs" style={{ color: AUTH.muted }}>
              <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> {t("authx.encrypted")}</span>
              <span className="opacity-40">·</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> {t("authx.securedArea")}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
